/**
 * Lean Setup Wizard — Page 2: People counts.
 *
 * Fields: number of technicians, service advisors, managers.
 * These drive Page 3 row generation for technicians; advisor/manager
 * counts are informational (saved to setup_wizard onboarding_state).
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { Page2Data } from '@/lib/onboarding/wizardValidation';
import { page2Schema } from '@/lib/onboarding/wizardValidation';

interface PeopleCountsStepProps {
  initial: Partial<Page2Data>;
  onBack: () => void;
  onNext: (data: Page2Data) => void;
}

export function PeopleCountsStep({ initial, onBack, onNext }: PeopleCountsStepProps) {
  const [numTechnicians, setNumTechnicians] = useState<number>(initial.num_technicians ?? 3);
  const [numAdvisors, setNumAdvisors] = useState<number>(initial.num_advisors ?? 1);
  const [numManagers, setNumManagers] = useState<number>(initial.num_managers ?? 1);
  const [errors, setErrors] = useState<string[]>([]);

  const handleNext = () => {
    const parsed = page2Schema.safeParse({
      num_technicians: Number(numTechnicians),
      num_advisors: Number(numAdvisors),
      num_managers: Number(numManagers),
    });
    if (!parsed.success) {
      setErrors(parsed.error.issues.map((i) => i.message));
      return;
    }
    setErrors([]);
    onNext(parsed.data);
  };

  return (
    <Card className="w-full max-w-2xl" data-testid="wizard-step-people-counts">
      <CardHeader>
        <CardTitle>Your Team</CardTitle>
        <CardDescription>
          Just the counts. You'll enter technician details on the next page; advisor + manager invites happen later from Settings.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="wizard-num-techs">Number of technicians</Label>
            <Input
              id="wizard-num-techs"
              type="number"
              min={1}
              value={numTechnicians}
              onChange={(e) => setNumTechnicians(Number(e.target.value))}
              data-testid="wizard-num-technicians"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="wizard-num-advisors">Number of service advisors</Label>
            <Input
              id="wizard-num-advisors"
              type="number"
              min={0}
              value={numAdvisors}
              onChange={(e) => setNumAdvisors(Number(e.target.value))}
              data-testid="wizard-num-advisors"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="wizard-num-managers">Number of managers</Label>
            <Input
              id="wizard-num-managers"
              type="number"
              min={0}
              value={numManagers}
              onChange={(e) => setNumManagers(Number(e.target.value))}
              data-testid="wizard-num-managers"
            />
          </div>
        </div>

        {errors.length > 0 && (
          <ul className="text-sm text-destructive list-disc pl-5" data-testid="wizard-step-errors">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        )}

        <div className="flex justify-between gap-2">
          <Button variant="outline" onClick={onBack}>Back</Button>
          <Button onClick={handleNext} data-testid="wizard-next">Next</Button>
        </div>
      </CardContent>
    </Card>
  );
}
